import { useRouter } from '@tanstack/react-router'
import type { Workspace } from '@botpress/client'
import { useMemo, useState, type ReactNode } from 'react'
import { DYNAMIC_QUOTAS, QuotaUsedPercentageWarningThreshold } from '~/features/usage/constants'
import { WorkspaceProfilePicture } from '~/features/workspaces/components'
import { useWorkspacesUsages } from '~/hooks'
import { useAppStore } from '~/stores'
import Fuse from 'fuse.js'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Input,
  showDialog,
  useSidebar,
} from '@bpinternal/ui-kit-next'
import { CircleAlert, Plus, Search, TriangleAlert } from 'lucide-react'
import { useDebounce } from 'react-use'
import { CreateWorkspaceDialog } from './CreateWorkspaceDialog'

type Props = { className?: string; workspaces: Workspace[]; children: ReactNode; asChild?: boolean }

export const WorkspacesDropdown = ({ workspaces, children, asChild = true }: Props) => {
  const activeWorkspace = useAppStore((s) => s.activeWorkspace)
  const navigate = useRouter().navigate
  const workspacesUsages = useWorkspacesUsages({
    workspaceIds: workspaces.map((w) => w.id),
    quotas: DYNAMIC_QUOTAS,
  })
  const [open, setOpen] = useState(false) // Need to controle the state for now because of a bug in radix, see: https://github.com/radix-ui/primitives/issues/837#issuecomment-2716644471
  const { isMobile, setOpenMobile } = useSidebar()

  const handleWorkspaceSelection = (workspaceId: string) => {
    if (isMobile) {
      setOpenMobile(false)
    }
    navigate({ to: '/workspaces/$workspaceId', params: { workspaceId } })
  }

  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')

  const filteredWorkspaces = useMemo(() => {
    if (!debouncedQuery.trim()) return workspaces || []

    const fuse = new Fuse(workspaces || [], {
      keys: ['name', 'id'],
      threshold: 0.3,
      includeScore: true,
    })

    const results = fuse.search(debouncedQuery)
    return results.map((result) => result.item)
  }, [workspaces, debouncedQuery])

  useDebounce(
    () => {
      setDebouncedQuery(searchQuery)
    },
    100,
    [searchQuery]
  )

  return (
    <DropdownMenu
      open={open}
      onOpenChange={(isOpen) => {
        setOpen(isOpen)
        if (!isOpen) {
          setSearchQuery('')
        }
      }}
    >
      <DropdownMenuTrigger asChild={asChild}>{children}</DropdownMenuTrigger>
      <DropdownMenuContent
        className="w-60"
        style={isMobile ? undefined : { maxHeight: 'min(50vh, var(--radix-dropdown-menu-content-available-height))' }}
      >
        <DropdownMenuLabel>Workspaces</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <div className="relative">
          <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search workspaces..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => {
              // Prevent the dropdown's built-in keyboard navigation from auto-selecting items
              e.stopPropagation()
            }}
            className="h-8 border-0 bg-transparent pl-8 shadow-none focus:border-0 focus:ring-0 focus-visible:ring-0"
            autoFocus
          />
        </div>
        {filteredWorkspaces
          ?.sort((a, b) => a.name.localeCompare(b.name))
          .map((workspace) => {
            const workspaceUsage = workspacesUsages[workspace.id]
            const anyUsageHasReachedQuota = Object.entries(workspaceUsage ?? {}).some(
              ([, usage]) => usage.percentage >= 100
            )
            const anyUsageExceedsWarningThreshold = !anyUsageHasReachedQuota
              ? Object.entries(workspaceUsage ?? {}).some(
                  ([, usage]) => usage.percentage > QuotaUsedPercentageWarningThreshold
                )
              : true
            return (
              <DropdownMenuItem
                key={workspace.id}
                disabled={workspace.id === activeWorkspace?.id}
                onClick={() => handleWorkspaceSelection(workspace.id)}
              >
                <WorkspaceProfilePicture
                  workspaceId={workspace.id}
                  className="h-5 w-5 rounded-full"
                  pictureUrl={workspace.profilePicture}
                />
                <span className="truncate">{workspace.name}</span>
                {anyUsageHasReachedQuota ? (
                  <CircleAlert className="ml-auto text-red-10" />
                ) : anyUsageExceedsWarningThreshold ? (
                  <TriangleAlert className="ml-auto text-amber-10" />
                ) : null}
              </DropdownMenuItem>
            )
          })}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={() => {
            setOpen(false)
            showDialog(({ close }) => <CreateWorkspaceDialog close={close} />, { title: 'Create a workspace' })
          }}
        >
          <Plus />
          <span>Create a workspace</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
